import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box, Grid, Typography, Button, TextField, Collapse, Breadcrumbs, Link,
  Select, MenuItem, FormControl, Drawer, IconButton, useMediaQuery, useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { GeoExpertServices } from './GeoExpertServices';
import { Helpers } from '../../../services/Helpers';

// ─── COLORS ──────────────────────────────────────────────────────────────────
const P  = '#4f46e5';
const PD = '#7c3aed';

const EXPERT_COLORS = [
  { color: '#4f46e5', light: '#ede9fe', border: '#c7d2fe' },
  { color: '#4f46e5', light: '#ede9fe', border: '#c7d2fe' },
  { color: '#4f46e5', light: '#ede9fe', border: '#c7d2fe' },
  { color: '#4f46e5', light: '#ede9fe', border: '#c7d2fe' },
  { color: '#4f46e5', light: '#ede9fe', border: '#c7d2fe' },
  { color: '#4f46e5', light: '#ede9fe', border: '#c7d2fe' },
];

// ─── API RESPONSE PARSERS ────────────────────────────────────────────────────
// Build catData map from API `data.services` array
// Response shape per category: { categoryId, categoryName, subCategories, expertCount, experts }
function buildCatData(services = []) {
  const catData = {};
  services.forEach((cat, ci) => {
    const colorSet = EXPERT_COLORS[ci % EXPERT_COLORS.length];
    let totalChildren = 0;
    let coveredChildren = 0;

    const heatmap = (cat.subCategories || []).map((sub) => {
      const masters = (sub.masterServices || []).map((ms) => {
        const childServices = ms.childServices || [];
        const coveredList   = childServices.filter(c => c.isCovered);
        totalChildren   += childServices.length;
        coveredChildren += coveredList.length;
        return {
          masterServiceId: ms.masterServiceId,
          name:    ms.masterServiceName,
          pct:     childServices.length ? Math.round((coveredList.length / childServices.length) * 100) : 0,
          covered: coveredList.length,
          total:   childServices.length,
          experts: (ms.experts || []).map(e => e.expertName),
          children: childServices.map(c => `${c.isCovered ? '✓' : '✗'} ${c.serviceName}`),
          uncoveredChildren: childServices.filter(c => !c.isCovered).map(c => c.serviceName),
        };
      });
      return { sub: sub.subcategoryName, subIcon: '🔧', subcategoryId: sub.subcategoryId, masters };
    });

    const coverage = totalChildren ? Math.round((coveredChildren / totalChildren) * 100) : 0;
    catData[cat.categoryName] = {
      id: cat.categoryId,
      icon: '🏷',
      ...colorSet,
      expertCount: cat.expertCount || 0,
      coverage,
      uncoveredCount: totalChildren - coveredChildren,
      heatmap,
      experts: (cat.experts || []).map(e => e.expertName),
    };
  });
  return catData;
}

// Build per-expert provided / missing service lists from services tree
function buildExpertServiceMap(services = [], expertsRaw = []) {
  const map = {};
  const idMap = {}; // any id variant → canonical map key
  expertsRaw.forEach(e => {
    const key = getExpertId(e);
    map[key] = { provided: [], missing: [] };
    [e.id, e._id, e.userId, e.expertId].forEach(v => { if (v) idMap[String(v)] = key; });
  });
  const canonical = (id) => idMap[String(id)] ?? String(id);

  // First pass: find which subcategories each expert actively works in
  // (has at least one covered child service in that subcategory)
  const expertSubcats = {}; // expertId → Set<subcategoryId>
  services.forEach((cat) => {
    (cat.subCategories || []).forEach((sub) => {
      (sub.masterServices || []).forEach((ms) => {
        (ms.childServices || []).forEach((cs) => {
          (cs.experts || []).forEach(e => {
            const eid = canonical(e.expertId);
            if (!expertSubcats[eid]) expertSubcats[eid] = new Set();
            expertSubcats[eid].add(sub.subcategoryId);
          });
        });
      });
    });
  });

  // Second pass: build provided + missing
  services.forEach((cat) => {
    (cat.subCategories || []).forEach((sub) => {
      (sub.masterServices || []).forEach((ms) => {
        (ms.childServices || []).forEach((cs) => {
          const coveredKeys = new Set((cs.experts || []).map(e => canonical(e.expertId)));

          // provided — experts who cover this child service
          coveredKeys.forEach(eid => {
            if (!map[eid]) return;
            let cb = map[eid].provided.find(c => c.catId === cat.categoryId);
            if (!cb) { cb = { catId: cat.categoryId, cat: cat.categoryName, catIcon: '🏷', subs: [] }; map[eid].provided.push(cb); }
            let sb = cb.subs.find(s => s.subId === sub.subcategoryId);
            if (!sb) { sb = { subId: sub.subcategoryId, sub: sub.subcategoryName, subIcon: '🔧', masters: [] }; cb.subs.push(sb); }
            let mb = sb.masters.find(m => m.masterServiceId === ms.masterServiceId);
            if (!mb) { mb = { masterServiceId: ms.masterServiceId, name: ms.masterServiceName, children: [] }; sb.masters.push(mb); }
            mb.children.push(cs.serviceName);
          });

          // missing — only for experts who work in this subcategory but don't cover this service
          expertsRaw.forEach(e => {
            const eid = getExpertId(e);
            if (coveredKeys.has(eid)) return;
            if (!expertSubcats[eid]?.has(sub.subcategoryId)) return; // not active in this subcategory
            let cb = map[eid].missing.find(c => c.catId === cat.categoryId);
            if (!cb) { cb = { catId: cat.categoryId, cat: cat.categoryName, catIcon: '🏷', subs: [] }; map[eid].missing.push(cb); }
            let sb = cb.subs.find(s => s.subId === sub.subcategoryId);
            if (!sb) { sb = { subId: sub.subcategoryId, sub: sub.subcategoryName, subIcon: '🔧', masters: [] }; cb.subs.push(sb); }
            let mb = sb.masters.find(m => m.masterServiceId === ms.masterServiceId);
            if (!mb) { mb = { masterServiceId: ms.masterServiceId, name: ms.masterServiceName, children: [] }; sb.masters.push(mb); }
            if (!mb.children.includes(cs.serviceName)) mb.children.push(cs.serviceName);
          });
        });
      });
    });
  });
  return map;
}

// Normalize expertsInRadius array using real API fields
function normalizeExperts(expertsRaw = [], serviceMap = {}) {
  return expertsRaw.map((e, i) => {
    const eid     = getExpertId(e);
    const svcData = serviceMap[eid] || { provided: [], missing: [] };
    return {
      id:       eid,
      name:     e.expertName || `${e.firstName || ''} ${e.lastName || ''}`.trim() || 'Expert',
      initials: mkInitials(e.expertName || `${e.firstName || ''} ${e.lastName || ''}`),
      phone:    e.mobileNumber || '',
      city:     e.city   || '',
      state:    e.state  || '',
      pincode:  e.pincode || '',
      address:  e.address || '',
      services: e.serviceCount || 0,
      dist:     +parseFloat(e.distanceKm || 0).toFixed(2),
      isActive: e.isActive,
      reviewStatus: String(e.reviewStatus ?? '0'),
      profileImage: e.profileImage,
      provided: svcData.provided,
      missing:  svcData.missing,
      cats:     [...new Set([
        ...svcData.provided.map(c => c.cat),
        ...svcData.missing.map(c => c.cat),
      ])],
      ...getExpColor(i),
    };
  });
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function getExpertId(e) { return String(e.id || e._id || e.userId || e.expertId || ''); }
function getExpColor(idx) { return EXPERT_COLORS[idx % EXPERT_COLORS.length]; }
function mkInitials(name = '') { return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2); }
function catInfo(pct) {
  if (pct >= 70) return { border: '#16a34a', label: 'GOOD',     bg: '#f0fdf4', color: '#16a34a' };
  if (pct >= 40) return { border: '#d97706', label: 'MODERATE', bg: '#fffbeb', color: '#d97706' };
  if (pct >= 1)  return { border: '#dc2626', label: 'LOW',      bg: '#fef2f2', color: '#dc2626' };
  return               { border: '#9ca3af', label: 'NO COVER', bg: '#f9fafb', color: '#9ca3af' };
}

const KEYFRAMES = `
  @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
  @keyframes spin   { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
`;

// ─── ATOMS ────────────────────────────────────────────────────────────────────
function AvatarComp({ expert, size = 38, active = false }) {
  const [imgError, setImgError] = React.useState(false);
  React.useEffect(() => { setImgError(false); }, [expert.profileImage]);
  const imgSrc = expert.profileImage && !imgError
    ? `${Helpers.ImagePathUrl()}/profile/sm/${expert.profileImage}`
    : null;

  return (
    <Box sx={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: imgSrc ? '#f3f4f6' : `linear-gradient(135deg, ${expert.color}, ${expert.color}bb)`,
      fontSize: size * 0.3, fontWeight: 900, color: '#fff',
      boxShadow: active ? `0 0 0 3px #fff, 0 0 0 5px ${expert.color}55` : `0 2px 8px ${expert.color}22`,
      border: active ? `2px solid ${expert.color}` : 'none',
      transition: 'all 0.2s', userSelect: 'none', overflow: 'hidden',
    }}>
      {imgSrc
        ? <Box component="img" src={imgSrc} alt={expert.name} onError={() => setImgError(true)}
            sx={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', display: 'block' }} />
        : (expert.initials || mkInitials(expert.name))
      }
    </Box>
  );
}

function ChipComp({ children, color, bg, border, size = 11 }) {
  return (
    <Box component="span" sx={{
      fontSize: size, fontWeight: 600, px: '10px', py: '3px',
      borderRadius: '99px', display: 'inline-flex', alignItems: 'center', gap: '3px',
      background: bg || `${color}12`, color, border: `1px solid ${border || `${color}30`}`,
    }}>
      {children}
    </Box>
  );
}

function BarComp({ pct, color, height = 5, bg = '#e5e7eb' }) {
  return (
    <Box sx={{ height, background: bg, borderRadius: 99, overflow: 'hidden' }}>
      <Box sx={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99, transition: 'width 0.7s ease' }} />
    </Box>
  );
}

function CardComp({ children, topColor, sx: sxProp = {} }) {
  return (
    <Box sx={{
      background: '#fff', borderRadius: '14px', border: `1px solid #e5e7eb`,
      borderTop: topColor ? `3px solid ${topColor}` : '1px solid #e5e7eb',
      boxShadow: '0 1px 6px rgba(0,0,0,.05)', overflow: 'hidden', ...sxProp,
    }}>
      {children}
    </Box>
  );
}

function PanelHead({ icon, title, right }) {
  return (
    <Box sx={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      px: '18px', py: '12px', borderBottom: '1px solid #f3f4f6', background: '#fafafa',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: 16 }}>{icon}</Typography>
        <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: '#111827' }}>{title}</Typography>
      </Box>
      {right && <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>{right}</Typography>}
    </Box>
  );
}

// ─── SERVICE HIERARCHY TREE ───────────────────────────────────────────────────
function ServiceHierarchy({ data = [], accentColor = P, mode = 'provided' }) {
  const [openSubs, setOpenSubs] = useState({});
  const isProvided = mode === 'provided';
  const toggle = (key) => setOpenSubs(s => ({ ...s, [key]: s[key] === false ? true : false }));

  if (!data.length) {
    return (
      <Box sx={{ py: 3, textAlign: 'center', color: '#9ca3af' }}>
        <Typography sx={{ fontSize: 13 }}>No services to display.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {data.map((cat, ci) => (
        <Box key={ci} sx={{ border: `1.5px solid ${accentColor}22`, borderRadius: '12px', overflow: 'hidden' }}>
          {/* Level 1 — Category */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2, py: 1.5, background: isProvided ? '#f0fdf4' : '#fffbeb',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 18 }}>{cat.catIcon}</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>{cat.cat}</Typography>
            </Box>
            <Box sx={{
              fontSize: 9, fontWeight: 700, px: '8px', py: '3px', borderRadius: '99px',
              textTransform: 'uppercase', background: `${accentColor}18`, color: accentColor, letterSpacing: '0.5px',
            }}>CATEGORY</Box>
          </Box>

          {/* Level 2 — Sub-categories */}
          {cat.subs?.map((sub, si) => {
            const key = `${ci}-${si}`;
            const isOpen = openSubs[key] !== false;
            return (
              <Box key={si}>
                <Box onClick={() => toggle(key)} sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  px: 2, py: 1.25, background: '#f9fafb', cursor: 'pointer',
                  borderBottom: isOpen ? '1px solid #f3f4f6' : 'none',
                  borderLeft: `3px solid ${accentColor}55`,
                  '&:hover': { background: '#f3f4f6' }, transition: 'all 0.15s',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 15 }}>{sub.subIcon}</Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#374151' }}>{sub.sub}</Typography>
                    <Box sx={{
                      fontSize: 9, fontWeight: 600, px: '7px', py: '2px', borderRadius: '99px',
                      textTransform: 'uppercase', background: '#f3f4f6', color: '#9ca3af', letterSpacing: '0.5px',
                    }}>Sub-Category</Box>
                  </Box>
                  <Typography sx={{ fontSize: 11, color: '#9ca3af', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▶</Typography>
                </Box>

                <Collapse in={isOpen}>
                  <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* Level 3 — Master Services */}
                    {sub.masters?.map((master, mi) => (
                      <Box key={mi} sx={{ background: '#fff', border: '1px solid #f3f4f6', borderRadius: '8px', px: 1.5, py: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: accentColor, flexShrink: 0 }} />
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#111827', flex: 1 }}>{master.name}</Typography>
                          <Box sx={{ fontSize: 9, fontWeight: 600, px: '7px', py: '2px', borderRadius: '99px', textTransform: 'uppercase', background: '#f3f4f6', color: '#6b7280', letterSpacing: '0.5px' }}>Master Service</Box>
                          <Box sx={{ fontSize: 10, fontWeight: 600, px: '8px', py: '2px', borderRadius: '99px', background: `${accentColor}18`, color: accentColor, border: `1px solid ${accentColor}30` }}>{master.children?.length} Services</Box>
                        </Box>
                        {/* Level 4 — Child Services */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pl: 2 }}>
                          {master.children?.map((child, chi) => (
                            <Box key={chi} component="span" sx={{
                              fontSize: 11.5, fontWeight: 600, px: '10px', py: '3px', borderRadius: '99px',
                              ...(isProvided
                                ? { background: '#f0fdf4', color: '#15803d', border: '1px solid #86efac' }
                                : { background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a' }),
                            }}>
                              {isProvided ? '✓' : '!'} {child}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

// ─── EXPERT DETAIL ────────────────────────────────────────────────────────────
function ExpertDetail({ expert }) {
  const [tab, setTab] = useState('provided');

  if (!expert) {
    return (
      <CardComp>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, color: '#9ca3af' }}>
          <Typography sx={{ fontSize: 42, mb: 1 }}>👈</Typography>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>Select an expert from the list</Typography>
          <Typography sx={{ fontSize: 12, mt: 0.5 }}>Click any expert to view their services</Typography>
        </Box>
      </CardComp>
    );
  }

  const provCount = expert.provided?.reduce((s, c) => s + c.subs.reduce((ss, sub) => ss + sub.masters.length, 0), 0) || 0;
  const missCount = expert.missing?.reduce((s, c) => s + c.subs.reduce((ss, sub) => ss + sub.masters.length, 0), 0) || 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Profile Card */}
      <CardComp topColor={expert.color}>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
            <AvatarComp expert={expert} size={64} active />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.25 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{expert.name}</Typography>
                <ChipComp
                  color={expert.reviewStatus === '1' ? '#16a34a' : expert.reviewStatus === '2' ? '#dc2626' : '#d97706'}
                  bg={expert.reviewStatus === '1' ? '#f0fdf4' : expert.reviewStatus === '2' ? '#fef2f2' : '#fffbeb'}
                  border={expert.reviewStatus === '1' ? '#86efac' : expert.reviewStatus === '2' ? '#fca5a5' : '#fde68a'}
                  size={10}>
                  {expert.reviewStatus === '1' ? '● Approved' : expert.reviewStatus === '2' ? '✗ Rejected' : '⏳ Review Pending'}
                </ChipComp>
                <ChipComp
                  color={expert.isActive ? '#16a34a' : '#9ca3af'}
                  bg={expert.isActive ? '#f0fdf4' : '#f9fafb'}
                  border={expert.isActive ? '#86efac' : '#e5e7eb'}
                  size={10}>
                  {expert.isActive ? '● Active' : '◦ Inactive'}
                </ChipComp>
              </Box>
              <Typography sx={{ fontSize: 12, color: '#6b7280', mb: 0.75 }}>{expert.phone}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                {expert.cats?.map((c, i) => <ChipComp key={i} color={expert.color}>{c}</ChipComp>)}
              </Box>
            </Box>
          </Box>

          <Box sx={{ height: 1, background: '#f3f4f6', mb: 2 }} />

          <Grid container spacing={1} sx={{ mb: 2 }}>
            {[
              ['🌆', 'City',     expert.city],
              ['📍', 'State',    expert.state || '—'],
              ['🚗', 'Distance', `${expert.dist} km`],
              ['🛠', 'Services', expert.services],
            ].map(([icon, label, val], i) => (
              <Grid item xs={3} key={i}>
                <Box sx={{ background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: '10px', py: 1, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 13 }}>{icon}</Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{val}</Typography>
                  <Typography sx={{ fontSize: 10, color: '#9ca3af' }}>{label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {expert.address && (
            <Box sx={{ fontSize: 11.5, color: '#6b7280', background: '#f9fafb', borderRadius: '8px', px: 1.5, py: 1 }}>
              📌 {expert.address}{expert.pincode ? ` — ${expert.pincode}` : ''}
            </Box>
          )}
        </Box>
      </CardComp>

      {/* Service Tabs */}
      <CardComp>
        <Box sx={{ display: 'flex', p: 1, gap: 0.75, background: '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
          {[
            { key: 'provided', label: '✅ Active Services', count: provCount, color: '#16a34a' },
            { key: 'missing',  label: '⚠️ Missing Services',    count: missCount, color: '#d97706' },
          ].map(t => (
            <Box key={t.key} onClick={() => setTab(t.key)} sx={{
              flex: 1, px: 1.5, py: 1, borderRadius: '9px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
              ...(tab === t.key ? { background: '#fff', boxShadow: '0 1px 6px rgba(0,0,0,.07)', color: t.color } : { color: '#9ca3af' }),
              transition: 'all 0.2s',
            }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'inherit' }}>{t.label}</Typography>
              <Box sx={{
                fontSize: 10, fontWeight: 700, px: '6px', py: '1px', borderRadius: '99px',
                background: tab === t.key ? `${t.color}18` : '#f3f4f6',
                color: tab === t.key ? t.color : '#6b7280',
              }}>{t.count}</Box>
            </Box>
          ))}
        </Box>
        <Box sx={{ p: 2 }}>
          {/* {tab === 'missing' && (
            <Box sx={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', p: 1.5, mb: 2 }}>
              <Typography sx={{ fontSize: 11.5, color: '#92400e' }}>
                💡 Services available on CuDel but not yet offered by this expert — potential gap to fill.
              </Typography>
            </Box>
          )} */}
          <ServiceHierarchy
            data={tab === 'provided' ? expert.provided : expert.missing}
            accentColor={expert.color}
            mode={tab}
          />
        </Box>
      </CardComp>
    </Box>
  );
}

// ─── EXPERT LIST ──────────────────────────────────────────────────────────────
function ExpertList({ experts = [], activeId, onSelect }) {
  const [showNoProvider, setShowNoProvider] = useState(false);
  const [fullCovOnly, setFullCovOnly]       = useState(false);
  const [sortDir, setSortDir]               = useState('asc');

  const noProviderCount = experts.filter(e => !e.services || e.services === 0).length;
  let filtered = showNoProvider
    ? experts.filter(e => !e.services || e.services === 0)
    : experts.filter(e => e.services > 0);
  if (fullCovOnly) filtered = filtered.filter(e => !e.missing || e.missing.length === 0);
  filtered = [...filtered].sort((a, b) => sortDir === 'asc' ? a.dist - b.dist : b.dist - a.dist);

  return (
    <CardComp>
      <PanelHead icon="👥" title="Experts Nearby" right={`${filtered.length} / ${experts.length} shown`} />

      {/* Filter + Sort Bar */}
      <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #f3f4f6', background: '#fafafa', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {/* Row 1 – Non-provider toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.75 }}>
          <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
            🚫 <strong>{noProviderCount}</strong> with no services
          </Typography>
          <Box onClick={() => setShowNoProvider(s => !s)} sx={{
            fontSize: 11, fontWeight: 700, px: '10px', py: '3px', borderRadius: '99px',
            border: '1.5px solid', cursor: 'pointer', userSelect: 'none', transition: 'all 0.2s',
            ...(showNoProvider
              ? { background: '#fef2f2', color: '#dc2626', borderColor: '#fca5a5' }
              : { background: '#f0fdf4', color: '#16a34a', borderColor: '#86efac' }),
          }}>
            {showNoProvider ? '🙈 Hide Non-Providers' : '👁 Show Non-Providers'}
          </Box>
        </Box>

        {/* Row 2 – Full coverage filter + Near/Far sort */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.75 }}>
          <Box onClick={() => setFullCovOnly(s => !s)} sx={{
            fontSize: 11, fontWeight: 700, px: '10px', py: '3px', borderRadius: '99px',
            border: '1.5px solid', cursor: 'pointer', userSelect: 'none', transition: 'all 0.2s',
            ...(fullCovOnly
              ? { background: '#ede9fe', color: '#4f46e5', borderColor: '#c7d2fe' }
              : { background: '#f3f4f6', color: '#6b7280', borderColor: '#e5e7eb' }),
          }}>
            {fullCovOnly ? '✅ Full Coverage Only' : '◻ Full Coverage Only'}
          </Box>
          <Box sx={{ display: 'flex', background: '#f3f4f6', borderRadius: '8px', p: '2px', gap: 0.25 }}>
            {[{ v: 'asc', label: '↑ Nearest' }, { v: 'desc', label: '↓ Farthest' }].map(opt => (
              <Box key={opt.v} onClick={() => setSortDir(opt.v)} sx={{
                px: '8px', py: '3px', borderRadius: '6px', cursor: 'pointer',
                fontSize: 10.5, fontWeight: 700, userSelect: 'none', transition: 'all 0.15s',
                ...(sortDir === opt.v
                  ? { background: '#fff', color: P, boxShadow: '0 1px 3px rgba(0,0,0,.08)' }
                  : { color: '#9ca3af' }),
              }}>{opt.label}</Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box>
        {filtered.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center', color: '#9ca3af' }}>
            <Typography sx={{ fontSize: 22, mb: 0.5 }}>🔍</Typography>
            <Typography sx={{ fontSize: 13 }}>No experts match the selected filters.</Typography>
          </Box>
        )}
        {filtered.map(expert => (
          <Box key={expert.id} onClick={() => onSelect(expert)} sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5,
            cursor: 'pointer', borderBottom: '1px solid #f9fafb',
            background: activeId === expert.id ? expert.light : 'transparent',
            border: activeId === expert.id ? `1.5px solid ${expert.color}44` : '1.5px solid transparent',
            transition: 'all 0.2s', '&:hover': { background: activeId === expert.id ? expert.light : '#f9fafb' },
          }}>
            <AvatarComp expert={expert} active={activeId === expert.id} />
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{expert.name}</Typography>
                {/* <ChipComp
                  color={expert.isActive ? '#16a34a' : '#9ca3af'}
                  bg={expert.isActive ? '#f0fdf4' : '#f9fafb'}
                  border={expert.isActive ? '#86efac' : '#e5e7eb'}
                  size={9}>
                  {expert.isActive ? '● Active' : '◦ Inactive'}
                </ChipComp> */}
              </Box>
              <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>📍 {expert.city}{expert.state ? ` · ${expert.state}` : ''} · {expert.dist} km</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
              <ChipComp
                color={expert.services > 0 ? expert.color : '#9ca3af'}
                bg={expert.services > 0 ? undefined : '#f9fafb'}
                border={expert.services > 0 ? undefined : '#e5e7eb'}>
                {expert.services > 0 ? `${expert.services} Services` : 'No Services'}
              </ChipComp>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <ChipComp
                  color={expert.reviewStatus === '1' ? '#16a34a' : expert.reviewStatus === '2' ? '#dc2626' : '#d97706'}
                  bg={expert.reviewStatus === '1' ? '#f0fdf4' : expert.reviewStatus === '2' ? '#fef2f2' : '#fffbeb'}
                  border={expert.reviewStatus === '1' ? '#86efac' : expert.reviewStatus === '2' ? '#fca5a5' : '#fde68a'}
                  size={10}>
                  {expert.reviewStatus === '1' ? '● Approved' : expert.reviewStatus === '2' ? '✗ Rejected' : '⏳ Review Pending'}
                </ChipComp>
                <ChipComp
                  color={expert.isActive ? '#16a34a' : '#9ca3af'}
                  bg={expert.isActive ? '#f0fdf4' : '#f9fafb'}
                  border={expert.isActive ? '#86efac' : '#e5e7eb'}
                  size={10}>
                  {expert.isActive ? '● Active' : '◦ Inactive'}
                </ChipComp>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </CardComp>
  );
}

// ─── KPI STRIP ────────────────────────────────────────────────────────────────
function KpiStrip({ stats, coverageFilter }) {
  const isUncovered = coverageFilter === 'uncovered';
  const subColor = isUncovered ? '#d97706' : '#0891b2';
  const subBg    = isUncovered ? '#fffbeb' : '#ecfeff';
  const msColor  = isUncovered ? '#d97706' : '#7c3aed';
  const msBg     = isUncovered ? '#fffbeb' : '#f5f3ff';
  const csColor  = isUncovered ? '#dc2626' : '#d97706';
  const csBg     = isUncovered ? '#fef2f2' : '#fef3c7';
  const subLabel = isUncovered ? 'uncovered / total' : 'covered / total';
  const cards = [
    { icon: '👥', label: 'Total Experts',    value: stats.totalExperts,  color: '#4f46e5', bg: '#ede9fe', sub: 'in radius', covered: null },
    { icon: '✅', label: 'Approved Experts', value: stats.activeExperts, color: '#15803d', bg: '#dcfce7', sub: 'review approved', covered: null },
    { icon: '🏷', label: 'Categories',       value: stats.categories,    color: '#0369a1', bg: '#e0f2fe', sub: 'parent categories', covered: null },
    { icon: '📂', label: 'Sub Categories',  value: stats.subcategories,  color: subColor, bg: subBg, sub: subLabel, covered: isUncovered ? stats.uncoveredSubCategories : stats.coveredSubCategories },
    { icon: '🛠', label: 'Master Services',  value: stats.masterServices, color: msColor,  bg: msBg,  sub: subLabel, covered: isUncovered ? stats.uncoveredMasterServices : stats.coveredMasterServices },
    { icon: '📋', label: 'Child Services',   value: stats.childServices,  color: csColor,  bg: csBg,  sub: subLabel, covered: isUncovered ? stats.uncoveredChildServices : stats.coveredChildServices },
  ];
  return (
    <Grid container spacing={1.5} sx={{ mb: 0 }}>
      {cards.map((c, i) => (
        <Grid item xs={6} sm={4} md={12 / 6} key={i} sx={{ flexBasis: { md: `${100 / 6}%` } }}>
          <Box sx={{
            background: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb',
            borderTop: `3px solid ${c.color}`, boxShadow: '0 1px 6px rgba(0,0,0,.04)',
            p: 2, animation: `fadeUp 0.4s ease ${i * 0.06}s both`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ width: 34, height: 34, borderRadius: '10px', background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                {c.icon}
              </Box>
              <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#9ca3af', letterSpacing: '0.5px', textAlign: 'right' }}>{c.label}</Typography>
            </Box>
            {c.covered != null ? (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '3px' }}>
                <Typography sx={{ fontSize: 26, fontWeight: 900, color: c.color, lineHeight: 1 }}>{c.covered}</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#9ca3af', lineHeight: 1 }}>/ {c.value}</Typography>
              </Box>
            ) : (
              <Typography sx={{ fontSize: 30, fontWeight: 900, color: c.color, lineHeight: 1 }}>{c.value}</Typography>
            )}
            <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5 }}>{c.sub}</Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
}

// ─── CATEGORY PICKER ──────────────────────────────────────────────────────────
function CategoryPicker({ catData, activeCat, onSelect, uncoveredMode = false }) {
  return (
    <CardComp>
      <PanelHead icon="📊" title="Categories" right={`${Object.keys(catData).length} total`} />
      <Box>
        {Object.entries(catData).filter(([, cat]) => uncoveredMode ? cat.coverage < 100 : cat.expertCount > 0).map(([key, cat]) => {
          const isActive = activeCat === key;
          const covColor = catInfo(cat.coverage);
          return (
            <Box key={key} onClick={() => onSelect(key)} sx={{
              px: 2, py: 1.5, cursor: 'pointer', borderBottom: '1px solid #f9fafb',
              background: isActive ? covColor.bg : 'transparent',
              outline: isActive ? `1.5px solid ${covColor.border}` : '1.5px solid transparent',
              transition: 'all 0.2s', '&:hover': { background: isActive ? covColor.bg : '#f9fafb' },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <Typography sx={{ fontSize: 20 }}>{cat.icon}</Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#111827' }}>{key}</Typography>
                  <Typography sx={{ fontSize: 10.5, color: '#6b7280' }}>👤 {cat.expertCount} experts · {cat.coverage}% covered</Typography>
                </Box>
                {isActive && (
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: covColor.color, flexShrink: 0 }} />
                )}
              </Box>
              <BarComp pct={cat.coverage} color={covColor.color} height={4} />
            </Box>
          );
        })}
      </Box>
    </CardComp>
  );
}

// ─── CATEGORY RESULTS ─────────────────────────────────────────────────────────
function CategoryResults({ catKey, catData, experts, onExpertClick, uncoveredMode = false }) {
  const [expandedMaster, setExpandedMaster] = useState(null);
  const cat = catData[catKey];
  if (!cat) return null;

  const R = 28, C = 34, strokeW = 5;
  const circ = 2 * Math.PI * R;
  // In uncovered mode show the ring as fully "gapped" (orange)
  const ringColor  = uncoveredMode ? '#d97706' : catInfo(cat.coverage).color;
  const ringPct    = uncoveredMode ? 100 : cat.coverage;
  const dash = (circ * ringPct) / 100;
  const covColor = uncoveredMode
    ? { color: '#d97706', bg: '#fffbeb', border: '#fde68a' }
    : catInfo(cat.coverage);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Category Banner */}
      <Box sx={{
        background: `linear-gradient(135deg, ${covColor.color}08, ${covColor.bg})`,
        border: `1.5px solid ${covColor.border}`, borderLeft: `5px solid ${covColor.color}`,
        borderRadius: '14px', p: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{
            width: 54, height: 54, borderRadius: '14px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', background: covColor.bg, border: `1.5px solid ${covColor.border}`, fontSize: 28, flexShrink: 0,
          }}>{cat.icon}</Box>
          <Box>
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', mb: 0.25 }}>
              {uncoveredMode ? 'UNCOVERED SERVICES' : 'CATEGORY ANALYSIS'}
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: '#111827', mb: 0.75 }}>{catKey}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {!uncoveredMode && <ChipComp color={covColor.color} bg={covColor.bg} border={covColor.border}>👤 {cat.expertCount} Experts</ChipComp>}
              <ChipComp color={covColor.color} bg={covColor.bg} border={covColor.border}>📂 {cat.heatmap?.length} Sub-categories</ChipComp>
              <ChipComp color={covColor.color} bg={covColor.bg} border={covColor.border}>🛠 {cat.heatmap?.reduce((s, h) => s + h.masters.length, 0)} Master Services</ChipComp>
              <ChipComp color={covColor.color} bg={covColor.bg} border={covColor.border}>📋 {cat.heatmap?.reduce((s, h) => s + h.masters.reduce((ss, m) => ss + (m.total || 0), 0), 0)} {uncoveredMode ? 'Uncovered' : 'Total'} Services</ChipComp>
            </Box>
          </Box>
        </Box>
        {/* SVG Ring */}
        <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width={C * 2} height={C * 2} style={{ transform: 'rotate(-90deg)' }}>
            <circle cx={C} cy={C} r={R} fill="none" stroke="#e5e7eb" strokeWidth={strokeW} />
            <circle cx={C} cy={C} r={R} fill="none" stroke={ringColor} strokeWidth={strokeW}
              strokeDasharray={`${dash} ${circ}`} style={{ transition: 'stroke-dasharray 0.8s' }} />
          </svg>
          <Box sx={{ position: 'absolute', textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 900, color: ringColor, lineHeight: 1 }}>
              {uncoveredMode ? cat.uncoveredCount : cat.coverage + '%'}
            </Typography>
            <Typography sx={{ fontSize: 8, color: '#9ca3af' }}>{uncoveredMode ? 'gaps' : 'covered'}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Heatmap */}
      <CardComp>
        <PanelHead icon={uncoveredMode ? '⚠️' : '🔥'} title={uncoveredMode ? 'Uncovered Services' : 'Coverage Heatmap'} />
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {(uncoveredMode
            ? cat.heatmap?.filter(section => section.masters?.some(m => m.covered < m.total))
            : cat.heatmap
          )?.map((section, si) => (
            <Box key={si}>
              {/* Sub-category divider */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{ height: 1, flex: 1, background: '#e5e7eb' }} />
                <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#9ca3af', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                  {section.subIcon} {section.sub}
                </Typography>
                <Box sx={{ height: 1, flex: 1, background: '#e5e7eb' }} />
              </Box>
              <Grid container spacing={1.5}>
                {(uncoveredMode
                  ? section.masters?.filter(m => m.covered < m.total)
                  : section.masters
                )?.map((master, mi) => {
                  const info = catInfo(master.pct);
                  const mKey = `${si}-${mi}`;
                  const isExpanded = expandedMaster === mKey;
                  return (
                    <Grid item xs={12} sm={6} key={mi}>
                      <Box sx={{
                        background: uncoveredMode ? '#fffbeb' : info.bg, borderRadius: '10px',
                        border: '1px solid #f3f4f6', borderLeft: `4px solid ${uncoveredMode ? '#d97706' : info.border}`,
                        overflow: 'hidden', transition: 'all 0.2s',
                        ...(!uncoveredMode && { cursor: 'pointer', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,.06)' } }),
                      }} onClick={uncoveredMode ? undefined : () => setExpandedMaster(isExpanded ? null : mKey)}>
                        <Box sx={{ p: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: uncoveredMode ? '#d97706' : info.border, flexShrink: 0 }} />
                              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#111827' }}>{master.name}</Typography>
                            </Box>
                            <Box sx={{ fontSize: 9, fontWeight: 700, px: '7px', py: '2px', borderRadius: '99px', textTransform: 'uppercase', letterSpacing: '0.5px',
                              background: uncoveredMode ? '#fef3c7' : `${info.border}30`,
                              color: uncoveredMode ? '#d97706' : info.color,
                            }}>{uncoveredMode ? 'UNCOVERED' : info.label}</Box>
                          </Box>
                          {!uncoveredMode && master.experts.length > 0 && (
                            <Typography sx={{ fontSize: 11, color: '#6b7280', mb: 0.75 }}>👤 {master.experts.join(' · ')}</Typography>
                          )}
                          {!uncoveredMode && <BarComp pct={master.pct} color={info.border} height={4} />}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: uncoveredMode ? 0 : 0.5 }}>
                            <Typography sx={{ fontSize: 10, color: '#9ca3af' }}>
                              {uncoveredMode
                                ? `${master.uncoveredChildren?.length ?? 0} uncovered`
                                : `${master.covered}/${master.total} services`}
                            </Typography>
                            {!uncoveredMode && (
                              <Typography sx={{ fontSize: 10, color: '#9ca3af' }}>{isExpanded ? '▲' : '▶'} expand</Typography>
                            )}
                          </Box>
                        </Box>
                        {/* In uncovered mode: show ✗ children directly without needing to expand */}
                        {uncoveredMode ? (
                          <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {master.uncoveredChildren?.map((name, ci) => (
                              <Box key={ci} component="span" sx={{
                                fontSize: 11, fontWeight: 600, px: '9px', py: '3px', borderRadius: '99px',
                                background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5',
                              }}>✗ {name}</Box>
                            ))}
                          </Box>
                        ) : (
                          <Collapse in={isExpanded}>
                            <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                              {master.children?.map((child, ci) => {
                                const covered = child.startsWith('✓');
                                return (
                                  <Box key={ci} component="span" sx={{
                                    fontSize: 11, fontWeight: 600, px: '9px', py: '3px', borderRadius: '99px',
                                    ...(covered
                                      ? { background: '#f0fdf4', color: '#15803d', border: '1px solid #86efac' }
                                      : { background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5' }),
                                  }}>{child}</Box>
                                );
                              })}
                            </Box>
                            {master.experts.length === 0 && (
                              <Box sx={{ mx: 1.5, mb: 1.5, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', p: 1.25 }}>
                                <Typography sx={{ fontSize: 11, color: '#92400e' }}>
                                  💡 Upsell opportunity — These services are live on CuDel but no expert provides them yet.
                                </Typography>
                              </Box>
                            )}
                          </Collapse>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ))}
        </Box>
      </CardComp>

      {/* Experts in Category — hidden in uncovered mode */}
      {!uncoveredMode && (() => {
        // Use cat.experts (names from API) as source of truth; fallback to cats array match
        const catExpertNames = new Set((cat.experts || []).map(n => n?.toLowerCase?.()));
        const catExperts = experts.filter(e =>
          catExpertNames.size > 0
            ? catExpertNames.has(e.name?.toLowerCase?.())
            : e.cats?.includes(catKey)
        );
        return (
          <CardComp>
            <PanelHead icon="👥" title={`Experts in ${catKey}`} right={`${catExperts.length} experts`} />
            <Box>
              {catExperts.length === 0 && (
                <Box sx={{ py: 4, textAlign: 'center', color: '#9ca3af' }}>
                  <Typography sx={{ fontSize: 22, mb: 0.5 }}>🙅</Typography>
                  <Typography sx={{ fontSize: 13 }}>No experts providing this category.</Typography>
                </Box>
              )}
              {catExperts.map(expert => (
                <Box key={expert.id} onClick={() => onExpertClick(expert)} sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5,
                  cursor: 'pointer', borderBottom: '1px solid #f9fafb',
                  transition: 'all 0.2s', '&:hover': { background: '#f9fafb' },
                }}>
                  <AvatarComp expert={expert} />
                  <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{expert.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>📍 {expert.city}{expert.state ? ` · ${expert.state}` : ''} · {expert.dist} km</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                    <ChipComp color={expert.color}>{expert.services} Services</ChipComp>
                    <ChipComp
                      color={expert.isActive ? '#16a34a' : '#9ca3af'}
                      bg={expert.isActive ? '#f0fdf4' : '#f9fafb'}
                      border={expert.isActive ? '#86efac' : '#e5e7eb'}
                      size={10}>
                      {expert.isActive ? '● Active' : '◦ Inactive'}
                    </ChipComp>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardComp>
        );
      })()}
    </Box>
  );
}

// ─── SEARCH FORM ──────────────────────────────────────────────────────────────
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', fontSize: 13.5,
    '& fieldset': { borderWidth: '1.5px' },
    '&.Mui-focused fieldset': { borderColor: P, borderWidth: '1.5px' },
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(79,70,229,.1)' },
  },
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function GeoExpertPage() {
  const { control, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: { lat: '', lng: '', radius: '', query: '', searchType: 'expert' },
  });

  const [searched, setSearched]           = useState(false);
  const [loading, setLoading]             = useState(false);
  const [errorMsg, setErrorMsg]           = useState('');
  const [view, setView]                   = useState('expert');
  const [experts, setExperts]             = useState([]);
  const [catData, setCatData]             = useState({});
  const [activeExpert, setActiveExpert]   = useState(null);
  const [activeCat, setActiveCat]         = useState('');
  const [searchCtx, setSearchCtx]         = useState(null);
  const [stats, setStats]                 = useState(null);
  const [drawerOpen, setDrawerOpen]       = useState(false);
  const [coverageFilter, setCoverageFilter] = useState('all');

  const theme   = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleExpertSelect = (e) => {
    setActiveExpert(e);
    if (isMobile) setDrawerOpen(true);
  };

  const handleCatSelect = (key) => {
    setActiveCat(key);
    if (isMobile) setDrawerOpen(true);
  };

  const searchType = watch('searchType');

  async function onSearch(values) {
    const { lat, lng, radius, query, searchType: sType } = values;
    if (!lat || !lng || !radius) return;

    setLoading(true);
    setErrorMsg('');
    try {
      const res = await GeoExpertServices.getActiveExpertsInRadius({
        lat, long: lng, radius,
        type: 'service',
        filterType: sType,
        key: query || undefined,
      });

      // Response shape: { success, data: { type, totalItems, expertsInRadius, summary, services } }
      const resData    = res?.data?.data || res?.data || {};
      const expertsRaw = Array.isArray(resData.expertsInRadius) ? resData.expertsInRadius : [];
      const services   = Array.isArray(resData.services)         ? resData.services        : [];
      const summary    = resData.summary || {};

      const serviceMap = buildExpertServiceMap(services, expertsRaw);
      let normalized   = normalizeExperts(expertsRaw, serviceMap);
      const builtCats  = buildCatData(services);

      if (query) {
        const q = query.toLowerCase();
        normalized = sType === 'expert'
          ? normalized.filter(e => e.name.toLowerCase().includes(q))
          : normalized.filter(e => e.cats.some(c => c.toLowerCase().includes(q)));
      }

      const firstCat = Object.keys(builtCats)[0] || '';
      setCatData(builtCats);
      setExperts(normalized);
      setActiveCat(firstCat);
      setCoverageFilter('all');
      setStats({
        totalExperts:           summary.totalExpertsInRadius ?? normalized.length,
        activeExperts:          normalized.filter(e => e.reviewStatus === '1').length,
        categories:             Object.keys(builtCats).length,
        subcategories:          summary.totalSubCategories ?? services.reduce((s, c) => s + (c.subCategories?.length || 0), 0),
        coveredSubCategories:   summary.coveredSubCategories   ?? 0,
        uncoveredSubCategories: summary.uncoveredSubCategories ?? 0,
        masterServices:         summary.totalMasterServices    ?? 0,
        coveredMasterServices:  summary.coveredMasterServices  ?? 0,
        uncoveredMasterServices: summary.uncoveredMasterServices ?? 0,
        childServices:          summary.totalChildServices     ?? 0,
        coveredChildServices:   summary.coveredChildServices   ?? 0,
        uncoveredChildServices: summary.uncoveredChildServices ?? 0,
      });
      setSearchCtx({ lat, lng, radius, count: normalized.length, total: summary.totalExpertsInRadius ?? normalized.length });
      setSearched(true);
      setActiveExpert(null);
      setView(sType === 'category' ? 'category' : 'expert');
    } catch (err) {
      setErrorMsg('Failed to fetch data. Check coordinates and try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', background: '#f3f4f6', p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      <style>{KEYFRAMES}</style>

      {/* ── Page Header ── */}
      <Box sx={{ mb: 2.5 }}>
        <Breadcrumbs sx={{ mb: 0.5 }}>
          <Link href="#" underline="hover" sx={{ fontSize: 11, color: '#9ca3af' }}>Admin Panel</Link>
          <Link href="#" underline="hover" sx={{ fontSize: 11, color: '#9ca3af' }}>Expert Listing</Link>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: P }}>Geo Expert</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: { xs: 20, md: 25 }, fontWeight: 900, color: '#111827', lineHeight: 1.2 }}>
              Geographic Expert Coverage
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#9ca3af', mt: 0.5 }}>
              Search experts by location radius or analyze category coverage gaps
            </Typography>
          </Box>

          {searched && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {/* View toggle — Expert tab hidden in uncovered mode */}
              {coverageFilter !== 'uncovered' && (
                <Box sx={{ display: 'flex', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', p: '4px', gap: 0.5 }}>
                  {[
                    { key: 'expert',   label: '👥 Expert View' },
                    { key: 'category', label: '📊 Category Intel' },
                  ].map(v => (
                    <Box key={v.key} onClick={() => setView(v.key)} sx={{
                      px: 1.5, py: 0.75, borderRadius: '9px', cursor: 'pointer', whiteSpace: 'nowrap',
                      fontSize: 12.5, fontWeight: v.key === view ? 700 : 500,
                      ...(v.key === view
                        ? { background: P, color: '#fff', boxShadow: `0 2px 8px rgba(79,70,229,.3)` }
                        : { color: '#6b7280', '&:hover': { background: '#f3f4f6' } }),
                      transition: 'all 0.2s',
                    }}>{v.label}</Box>
                  ))}
                </Box>
              )}
              {/* Coverage filter — client-side, no API re-call */}
              <Box sx={{ display: 'flex', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', p: '4px', gap: 0.5 }}>
                {[
                  { val: 'all',       lbl: '🌐 All',        activeColor: P },
                  { val: 'uncovered', lbl: '⚠️ Uncovered', activeColor: '#d97706' },
                ].map(opt => (
                  <Box key={opt.val} onClick={() => {
                    setCoverageFilter(opt.val);
                    if (opt.val === 'uncovered') setView('category');
                  }} sx={{
                    px: 1.5, py: 0.75, borderRadius: '9px', cursor: 'pointer', whiteSpace: 'nowrap',
                    fontSize: 12.5, fontWeight: coverageFilter === opt.val ? 700 : 500,
                    ...(coverageFilter === opt.val
                      ? { background: opt.val === 'uncovered' ? '#fffbeb' : opt.val === 'covered' ? '#f0fdf4' : '#ede9fe',
                          color: opt.activeColor, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }
                      : { color: '#6b7280', '&:hover': { background: '#f3f4f6' } }),
                    transition: 'all 0.2s',
                  }}>{opt.lbl}</Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Search Form Card ── */}
      <CardComp sx={{ mb: 2 }}>
        <PanelHead icon="🔍" title="Location Search" right="Enter coordinates to find experts" />
        <Box component="form" onSubmit={handleSubmit(onSearch)} sx={{ p: 2 }}>
          <Grid container spacing={1.5} sx={{ mb: 1.5 }} alignItems="flex-end">

            {/* Latitude */}
            <Grid item xs={12} sm={4} md={3}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#374151', mb: 0.5 }}>📍 Latitude</Typography>
              <Controller name="lat" control={control} rules={{ required: true }}
                render={({ field }) => (
                  <TextField {...field} size="small" placeholder="e.g. 28.6139" fullWidth error={!!errors.lat} sx={inputSx} />
                )}
              />
            </Grid>

            {/* Longitude */}
            <Grid item xs={12} sm={4} md={3}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#374151', mb: 0.5 }}>📍 Longitude</Typography>
              <Controller name="lng" control={control} rules={{ required: true }}
                render={({ field }) => (
                  <TextField {...field} size="small" placeholder="e.g. 77.2090" fullWidth error={!!errors.lng} sx={inputSx} />
                )}
              />
            </Grid>

            {/* Radius */}
            <Grid item xs={12} sm={4} md={2}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#374151', mb: 0.5 }}>📡 Radius (KM)</Typography>
              <Controller name="radius" control={control} rules={{ required: true }}
                render={({ field }) => (
                  <FormControl fullWidth size="small" error={!!errors.radius}>
                    <Select
                      {...field}
                      displayEmpty
                      sx={{
                        borderRadius: '10px', fontSize: 13.5,
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: '1.5px' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: P, borderWidth: '1.5px' },
                        '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(79,70,229,.1)' },
                      }}
                    >
                      <MenuItem value="" disabled><em style={{ color: '#9ca3af' }}>Select km</em></MenuItem>
                      {Array.from({ length: 15 }, (_, i) => i + 1).map(n => (
                        <MenuItem key={n} value={String(n)}>{n} km</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Search By Toggle */}
            <Grid item xs={12} sm="auto">
              <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#374151', mb: 0.5 }}>Search By</Typography>
              <Controller name="searchType" control={control}
                render={({ field }) => (
                  <Box sx={{ display: 'flex', background: '#f3f4f6', borderRadius: '10px', p: '3px' }}>
                    {[{ val: 'expert', lbl: '👤 Expert' }, { val: 'category', lbl: '🏷 Category' }].map(opt => (
                      <Box key={opt.val} onClick={() => field.onChange(opt.val)} sx={{
                        px: 1.5, py: 0.75, borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap',
                        fontSize: 12.5, fontWeight: field.value === opt.val ? 700 : 500,
                        ...(field.value === opt.val
                          ? { background: '#fff', color: P, boxShadow: '0 1px 4px rgba(0,0,0,.1)' }
                          : { color: '#6b7280' }),
                        transition: 'all 0.2s',
                      }}>{opt.lbl}</Box>
                    ))}
                  </Box>
                )}
              />
            </Grid>

            {/* Query */}
            <Grid item xs={12} sm>
              <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#374151', mb: 0.5 }}>
                {searchType === 'expert' ? '👤 Expert Name (optional)' : '🏷 Category Name (optional)'}
              </Typography>
              <Controller name="query" control={control}
                render={({ field }) => (
                  <TextField {...field} size="small"
                    placeholder={searchType === 'expert' ? 'Search' : 'e.g. Beauty'}
                    fullWidth sx={inputSx}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(onSearch)(); } }}
                  />
                )}
              />
            </Grid>

            {/* Search Button */}
            <Grid item xs={12} sm="auto">
              <Button type="submit" disabled={loading} sx={{
                height: 44, px: 3, borderRadius: '10px', fontWeight: 800, fontSize: 13.5,
                whiteSpace: 'nowrap', minWidth: 140,
                ...(loading
                  ? { background: '#e5e7eb', color: '#9ca3af', cursor: 'not-allowed', pointerEvents: 'none' }
                  : {
                    background: `linear-gradient(135deg, ${P}, ${PD})`,
                    color: '#fff', boxShadow: `0 4px 14px rgba(79,70,229,.3)`,
                    '&:hover': { boxShadow: `0 6px 18px rgba(79,70,229,.4)`, transform: 'translateY(-1px)' },
                    '&:active': { transform: 'translateY(0)' },
                  }),
                transition: 'all 0.2s',
              }}>
                {loading
                  ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 14, height: 14, borderRadius: '50%', border: '2.5px solid #9ca3af44', borderTop: '2.5px solid #9ca3af', animation: 'spin 0.7s linear infinite' }} />
                      Searching…
                    </Box>
                  : '🔍 Search'}
              </Button>
            </Grid>
          </Grid>

          {/* Hint / Error bar */}
          {errorMsg ? (
            <Box sx={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '9px', px: 2, py: 1 }}>
              <Typography sx={{ fontSize: 12, color: '#dc2626' }}>⚠️ {errorMsg}</Typography>
            </Box>
          ) : !searched && (
            <Box sx={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '9px', px: 2, py: 1 }}>
              <Typography sx={{ fontSize: 12, color: '#1d4ed8' }}>
                💡 Try: Lat 26.8785677 · Long 80.9843048 · Radius 100 to see Lucknow results
              </Typography>
            </Box>
          )}
        </Box>
      </CardComp>

      {/* ── Results ── */}
      {searched && stats && (
        <>
          <KpiStrip stats={stats} coverageFilter={coverageFilter} />

          {/* Context Pills */}
          {searchCtx && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, mb: 2 }}>
              <ChipComp color="#0369a1" bg="#eff6ff" border="#bfdbfe" size={11.5}>
                📍 {parseFloat(searchCtx.lat).toFixed(4)}°N, {parseFloat(searchCtx.lng).toFixed(4)}°E
              </ChipComp>
              <ChipComp color="#15803d" bg="#f0fdf4" border="#86efac" size={11.5}>
                📡 {searchCtx.radius} KM Radius
              </ChipComp>
              <ChipComp color={P} bg="#ede9fe" border="#c7d2fe" size={11.5}>
                👥 {searchCtx.count} matched · {searchCtx.total} total in radius
              </ChipComp>
              {/* <ChipComp color="#7c3aed" bg="#f5f3ff" border="#ddd6fe" size={11.5}>
                🏷 type=service
              </ChipComp> */}
            </Box>
          )}

          {/* Expert View — hidden when uncovered filter is active */}
          {view === 'expert' && coverageFilter !== 'uncovered' && (
            <Grid container spacing={1.75}>
              <Grid item xs={12} md={4} lg={4}>
                <Box sx={{ maxHeight: { md: 'calc(100vh - 200px)' }, overflowY: { md: 'auto' }, borderRadius: '14px' }}>
                  <ExpertList
                    experts={experts}
                    activeId={activeExpert?.id}
                    onSelect={handleExpertSelect}
                  />
                </Box>
              </Grid>
              {!isMobile && (
                <Grid item md={8} lg={8}>
                  <Box sx={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', borderRadius: '14px' }}>
                    <ExpertDetail expert={activeExpert} />
                  </Box>
                </Grid>
              )}
            </Grid>
          )}

          {/* Category Intel View */}
          {view === 'category' && Object.keys(catData).length > 0 && (
            <Grid container spacing={1.75}>
              <Grid item xs={12} md={3} lg={2.5}>
                <Box sx={{ maxHeight: { md: 'calc(100vh - 200px)' }, overflowY: { md: 'auto' }, borderRadius: '14px' }}>
                  <CategoryPicker
                    catData={catData}
                    activeCat={activeCat}
                    onSelect={handleCatSelect}
                    uncoveredMode={coverageFilter === 'uncovered'}
                  />
                </Box>
              </Grid>
              {!isMobile && (
                <Grid item md={9} lg={9.5}>
                  <Box sx={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', borderRadius: '14px' }}>
                    <CategoryResults
                      catKey={activeCat}
                      catData={catData}
                      experts={experts}
                      onExpertClick={(e) => { setActiveExpert(e); setView('expert'); }}
                      uncoveredMode={coverageFilter === 'uncovered'}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
          {view === 'category' && Object.keys(catData).length === 0 && (
            <CardComp sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 32, mb: 1 }}>📭</Typography>
              <Typography sx={{ fontSize: 14, color: '#6b7280', fontWeight: 600 }}>
                No category/service data returned for this radius. Try a larger radius or different coordinates.
              </Typography>
            </CardComp>
          )}

          {/* ── Mobile Right Drawer ── */}
          <Drawer
            anchor="right"
            open={isMobile && drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{
              sx: {
                width: '92vw',
                maxWidth: 480,
                p: 0,
                background: '#f3f4f6',
                display: 'flex',
                flexDirection: 'column',
              }
            }}
          >
            {/* Sticky close header */}
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 2, py: 1.25, background: '#fff', borderBottom: '1px solid #e5e7eb',
              flexShrink: 0, position: 'sticky', top: 0, zIndex: 10,
            }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, mr: 1 }}>
                {view === 'expert' ? (activeExpert?.name || 'Expert Detail') : (activeCat || 'Category Detail')}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setDrawerOpen(false)}
                sx={{
                  background: '#f3f4f6', border: '1px solid #e5e7eb',
                  '&:hover': { background: '#e5e7eb' },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ p: 1.5, overflowY: 'auto', flex: 1 }}>
              {view === 'expert' && <ExpertDetail expert={activeExpert} />}
              {view === 'category' && (
                <CategoryResults
                  catKey={activeCat}
                  catData={catData}
                  experts={experts}
                  onExpertClick={(e) => { setActiveExpert(e); setView('expert'); setDrawerOpen(true); }}
                  uncoveredMode={coverageFilter === 'uncovered'}
                />
              )}
            </Box>
          </Drawer>
        </>
      )}
    </Box>
  );
}
